import React, { useState, useEffect, useRef } from 'react';
import {
  Lock,
  UserCheck,
  Server,
  Fingerprint,
  Eye,
  Key,
  CheckCircle,
  Globe,
  Building,
  Leaf,
  FileText,
  Shield,
  Mail,
  Database,
  Clock,
  Scale
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/buttons/BackButton';
import HeaderNoAuth from '../components/Header/HeaderNoAuth';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import Card from '../components/Card/Card';
import Button from '../components/buttons/Button';
import './Privacy.css';

// Swiss Map Visualization Component
function SwissMapVisualization({ className = '' }) {
  const containerRef = useRef(null);
  const [animationStep, setAnimationStep] = useState(0);

  useEffect(() => {
    // Simple periodic animation cycle
    const animationCycle = () => {
      setAnimationStep(0);
      
      setTimeout(() => setAnimationStep(1), 500);   // Doctor appears
      setTimeout(() => setAnimationStep(2), 1500);  // Arrow + Server appear
      setTimeout(() => setAnimationStep(3), 2500);  // Arrow + AI appear
      setTimeout(() => setAnimationStep(0), 4000);  // Reset
    };

    // Start the animation cycle
    animationCycle();
    
    // Repeat every 5 seconds
    const interval = setInterval(animationCycle, 5000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={containerRef} className={`swiss-map-container ${className}`}>
      <div className="map-wrapper">
        {/* Swiss Map Background */}
        <div className="swiss-map-bg">
          <img src={require('./swissmap.png')} alt="Switzerland Map" className="swiss-map" />
          <div className="map-overlay"></div>
        </div>
        
        {/* Doctor icon - left */}
        <div className={`map-icon doctor-icon ${animationStep >= 1 ? 'active' : ''}`}>
          <div className="png-icon">
            <img src={require('./doctor.png')} alt="Doctor" />
          </div>
        </div>
        
        {/* Arrow from doctor to server */}
        <div className={`arrow arrow-1 ${animationStep >= 2 ? 'show' : ''}`}>
          <img src={require('./arrow.png')} alt="Arrow" className="arrow-img" />
        </div>
        
        {/* Server icon - center */}
        <div className={`map-icon server-icon ${animationStep >= 2 ? 'active' : ''}`}>
          <div className="png-icon">
            <img src={require('./server.png')} alt="Server" />
          </div>
        </div>
        
        {/* Arrow from server to AI */}
        <div className={`arrow arrow-2 ${animationStep >= 3 ? 'show' : ''}`}>
          <img src={require('./arrow.png')} alt="Arrow" className="arrow-img" />
        </div>
        
        {/* AI icon - right */}
        <div className={`map-icon ai-icon ${animationStep >= 3 ? 'active' : ''}`}>
          <div className="png-icon">
            <img src={require('./ai.png')} alt="AI" />
          </div>
        </div>
        
      </div>
      
      {/* Locker outside the map */}
      <div className={`locker-container ${animationStep >= 1 ? 'active' : ''}`}>
        <Lock className="locker-icon" />
        <div className="locker-text">
          <strong>Protection totale</strong>
          <span>Vos données restent en Suisse</span>
        </div>
      </div>
    </div>
  );
}

function PrivacyPage() {
  const { isSubscribed, profile } = useAuth();
  const navigate = useNavigate();
  const isAuthenticated = isSubscribed || profile;
  const [activeSection, setActiveSection] = useState(null);

  const principles = [
    {
      icon: Server,
      title: "Hébergement Suisse",
      description: "Toutes vos données sont hébergées exclusivement en Suisse. Aucun transfert en dehors de la Suisse ou de l'UE sans votre consentement.",
      gradient: "linear-gradient(135deg, #DC143C 0%, #8B0000 100%)"
    },
    {
      icon: Lock,
      title: "Données protégées",
      description: "Vous restez maître de vos données. Elles ne servent qu'à améliorer notre service pour vous seulement, et ne sont jamais utilisées pour entraîner nos modèles généraux.",
      gradient: "linear-gradient(135deg, #FF6B6B 0%, #DC143C 100%)"
    },
    {
      icon: Shield,
      title: "Chiffrement AES-256",
      description: "Protection maximale avec chiffrement AES-256 en transit et au repos. Rotation automatique et régulière des clés de sécurité.",
      gradient: "linear-gradient(135deg, #DC143C 0%, #B22222 100%)"
    },
    {
      icon: Fingerprint,
      title: "Accès strictement contrôlé",
      description: "Authentification 2FA, segmentation des bases de données, similaires aux applications bancaires.",
      gradient: "linear-gradient(135deg, #B22222 0%, #8B0000 100%)"
    }
  ];

  const certifications = [
    {
      icon: Building,
      title: "Conformité LPD",
      description: "Conformité avec la Loi fédérale sur la protection des données (LPD)"
    },
    {
      icon: CheckCircle,
      title: "Conformité RGPD",
      description: "Respect du Règlement Général sur la Protection des Données (RGPD) de l'Union Européenne"
    },
    {
      icon: Globe,
      title: "Standards internationaux",
      description: "Nos fournisseurs sont certifiés ISO 27001 pour la gestion de la sécurité de l'information"
    },
    {
      icon: Leaf,
      title: "Énergies renouvelables",
      description: "Serveurs alimentés par 100% d'énergies renouvelables suisses pour un impact environnemental minimal"
    }
  ];

  const dataTypes = [
    {
      type: "Données d'identité",
      items: [
        "Nom, prénom, spécialisation",
        "Numéro de téléphone (pour authentification 2FA)",
        "Coordonnées de facturation"
      ],
      protection: "Chiffrement AES-256, accès strictement contrôlé, nécessaires à la gestion du compte"
    },
    {
      type: "Données de contact et compte",
      items: [
        "Adresse e-mail",
        "Identifiants de connexion",
        "Journaux de connexion (logs)",
        "Paramètres d'application"
      ],
      protection: "Sécurisées par 2FA, chiffrement en transit et au repos, logs conservés temporairement"
    },
    {
      type: "Documents médicaux",
      items: [
        "Modèles de consultation",
        "Rapports générés",
        "Dictées vocales (supprimées immédiatement après traitement)"
      ],
      protection: "Chiffrement AES-256, suppression immédiate des fichiers audio/image après génération"
    },
    {
      type: "Données patients",
      items: [
        "Informations médicales (identifiants directs INTERDITS)",
        "Contenu des rapports"
      ],
      protection: "Données sensibles, traitement strictement limité à la génération de rapports, supprimables à tout moment"
    },
    {
      type: "Données techniques",
      items: [
        "Logs système",
        "Métriques d'utilisation",
        "Retours et correspondances"
      ],
      protection: "Anonymisées après période limitée, utilisées pour amélioration du service uniquement"
    }
  ];

  return (
    <div className="privacy-page">
      {!isAuthenticated && <HeaderNoAuth />}
      {!isAuthenticated && <BackButton to="/landing" />}
      {isAuthenticated && <BackButton to="/guide" />}

      {/* Header with Swiss Map */}
      <header className="privacy-header">
        <h1>Déclaration de protection des données</h1>
        <p>
          La protection de vos données personnelles et de celles de vos patients est au cœur de nos priorités.
          Nous respectons la LPD suisse et le RGPD européen avec des mesures techniques et organisationnelles strictes.
        </p>
        <SwissMapVisualization />
      </header>

      <div className="settings-container">
        {/* Legal Documents Links */}
        <Card className="card-large">
          <div className="privacy-intro">
            <h2>Documents complets</h2>
            <p style={{ marginBottom: '20px' }}>
              Cette page présente seulement un résumé de notre politique de protection des données.
              Pour une information complète et juridiquement contraignante, consultez les documents complets :
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button
                variant="secondary"
                type="default"
                size="medium"
                icon={Shield}
                onClick={() => window.open('https://voxia-md.ch/public/cg/dpd.pdf', '_blank')}
              >
                Déclaration de protection des données
              </Button>
              <Button
                variant="secondary"
                type="default"
                size="medium"
                icon={FileText}
                onClick={() => window.open('https://voxia-md.ch/public/cg/cgu.pdf', '_blank')}
              >
                Conditions générales d'utilisation
              </Button>
              <Button
                variant="secondary"
                type="default"
                size="medium"
                icon={Scale}
                onClick={() => window.open('https://voxia-md.ch/public/cg/cgv.pdf', '_blank')}
              >
                Conditions générales de vente
              </Button>
            </div>
          </div>
        </Card>

        {/* Core Principles */}
        <Card className="card-large">
          <SectionHeader icon={Server} title="Nos engagements clés" />
          <div className="principles-grid">
            {principles.map((principle, index) => {
              const Icon = principle.icon;
              return (
                <div 
                  key={index}
                  className="principle-card"
                  style={{ '--gradient': principle.gradient }}
                  onMouseEnter={() => setActiveSection(index)}
                  onMouseLeave={() => setActiveSection(null)}
                >
                  <div className="principle-header">
                    <div className="principle-icon">
                      <Icon />
                    </div>
                    <h3>{principle.title}</h3>
                  </div>
                  <p>{principle.description}</p>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Certifications Section */}
        <Card className="card-large">
          <SectionHeader icon={CheckCircle} title="Certifications & Conformité" />
          <div className="certifications-grid">
            {certifications.map((cert, index) => {
              const Icon = cert.icon;
              return (
                <div key={index} className="certification-item">
                  <div className="certification-icon">
                    <Icon />
                  </div>
                  <h4>{cert.title}</h4>
                  <p>{cert.description}</p>
                </div>
              );
            })}
          </div>
        </Card>


        {/* Your Rights Section */}
        <Card className="card-large">
          <SectionHeader icon={UserCheck} title="Vos droits" />


          <div className="principles-grid">
            <div className="principle-card">
              <div className="principle-header">
                <div className="principle-icon" style={{ background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)' }}>
                  <Eye />
                </div>
                <h3>Droit d'accès</h3>
              </div>
              <p>Obtenir la confirmation du traitement de vos données et accéder à leur contenu.</p>
            </div>

            <div className="principle-card">
              <div className="principle-header">
                <div className="principle-icon" style={{ background: 'linear-gradient(135deg, #2196F3 0%, #1565C0 100%)' }}>
                  <Key />
                </div>
                <h3>Droit de rectification</h3>
              </div>
              <p>Faire corriger des données inexactes ou incomplètes.</p>
            </div>

            <div className="principle-card">
              <div className="principle-header">
                <div className="principle-icon" style={{ background: 'linear-gradient(135deg, #FF9800 0%, #E65100 100%)' }}>
                  <UserCheck />
                </div>
                <h3>Droit à l'effacement</h3>
              </div>
              <p>Demander la suppression des données à tout moment</p>
            </div>
          </div>

        </Card>


        {/* Contact and Information */}
        <Card className="card-large">
          <SectionHeader icon={Mail} title="Contact et informations" />
          <div className="privacy-section">
            <h3>Fournisseur</h3>
            <p>
              <strong>GBM Medical SA</strong><br />
              Chemin des Bûcherons 116<br />
              1234 Vessy, Genève, Suisse<br />
              IDE : CHE-410.905.318
            </p>

            <h3>Questions sur la protection des données</h3>
            <p style={{ marginBottom: '16px' }}>
              Pour toute question ou demande relative à la protection des données, vous pouvez nous contacter notre DPO :
            </p>
            <Button
              variant="primary"
              type="default"
              size="large"
              icon={Mail}
              onClick={() => navigate('/contact')}
            >
              Contacter le DPO
            </Button>
          </div>
        </Card>

      </div>
    </div>
  );
}

export default PrivacyPage;